import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';
// import { setCookie } from 'react-use-cookie';

// import { check_subdomain } from '../../../lib/server_helper';
import { fetch_one, fetch_all } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import {useDraggable, useDroppable, DragDropProvider} from '@dnd-kit/react';

import {
  Box,
  Button as MuiButton,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  IconButton,
  Typography
} from '../../../components/v2/styled';

import { User } from '../../../models/User';
import Close from '@mui/icons-material/Close';
import Settings from '@mui/icons-material/Settings';
import { SnackSaved } from '../../../components/v2/snacks/Saved';
import { Base } from '../../../components/v2/dialogs/Base';
import {
  WIDGETS_WITH_SETTINGS,
  getWidgetSettingsComponent,
  getWidgetStatusComponent
} from './widget_settings/WidgetSettingsMapper';
import HelpersClientWidgets from '../../../actions/helpers/client_widgets';

// const { DateTime } = require("luxon");

type Props = {}

export const CoachSettingsUserDetailWidgets: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  // const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const queryClient = useQueryClient();
  // const { state } = useLocation();

  // const [flickerEditor, setFlickerEditor] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<number>(login?.id || 0);
  const [extendedUser, setExtendedUser] = useState<User>();

  const fields = [
    'id', 'first_name', 'last_name', 'auth_token', 'email', 'is_admin', 'extra_data', 'get_item_thumb', 'get_item', 'type', 'settings',
    'practice|id/name/item_is_square/get_medium', 'calendar_url', 'birth_date', 'get_bg_medium', 'profile_text', 'is_background_colour',
    'gender', 'street', 'number', 'zip', 'city', 'country_id', 'phone_mobile', 'phone_fixed', 'coach_skills', 'background_colour', 'ehealth',
    'get_medium', 'calendar_url', 'type', 'ehealth_cert_complete', 'is_super', 'mfa_status', 'otp_provisioning'
  ];

  const [notTheFirstTime, setNotTheFirstTime] = useState<number>(0);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [settingsOpenFor, setSettingsOpenFor] = useState<string | null>(null);
  const [settingsDraft, setSettingsDraft] = useState<any>(null);
  // Draft for the goto_links order setting (a sibling of the visibility map
  // under settings). Generalised per-widget when more widgets gain an order.
  const [settingsOrderDraft, setSettingsOrderDraft] = useState<string[] | null>(null);

  // Source of truth for which widgets have a settings UI.
  const widgetsWithSettings = WIDGETS_WITH_SETTINGS;
  const [leftCol, setLeftCol] = useState<string[]>([]);
  const [midCol, setMidCol] = useState<string[]>([]);
  const [rightCol, setRightCol] = useState<string[]>([]);

  const {
    data: extendedUserTemp,
    isLoading: extendedUserLoading,
    isError: extendedUserError,
    isSuccess: extendedUserSuccess,
    refetch: aextendedUserRefetch,
  } = useQuery({
    queryKey: ["user", 'extended', 'otp', activeId],
    queryFn: () =>
      fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user',
          fields: fields,
          id: activeId
        },
        login
      ),
      // select: (d) => {
      //   return d.custom_result;
      // },
    enabled: !!activeId,
  });










  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsEditPasswordError(false);
      // setEditPasswordSuccess(false);
      // setEditPasswordError(undefined);
      // setEditPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsEditPasswordError(true);
      // setEditPasswordError(data as any);
      // setEditPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      aextendedUserRefetch();
      setSavedOpen(true);
      if (activeId === login?.id) setLogin(data);
    },
  });










  // Per-widget settings live alongside the layout in user.settings.userdetails_widgets,
  // under a `settings` map keyed by widget key. Widgets are unique across columns
  // (the palette hides any key already placed), so one entry per key is unambiguous.
  const getWidgetSettings = (widgetKey: string): any => {
    return extendedUser?.settings?.userdetail_widgets?.settings?.[widgetKey] || {};
  };

  // The order setting key for the currently open widget, or null when it has none.
  // Generalised per-widget via the helper lookup so any widget with an order
  // setting (goto_links, actions, ...) is handled the same way.
  const widgetOrderKey = settingsOpenFor
    ? HelpersClientWidgets.getOrderKeyForWidget(settingsOpenFor)
    : null;
  const getWidgetOrder = (widgetKey: string): string[] => {
    const orderKey = HelpersClientWidgets.getOrderKeyForWidget(widgetKey);
    if (!orderKey) return [];
    return extendedUser?.settings?.userdetail_widgets?.settings?.[orderKey]
      || [...HelpersClientWidgets.getDefaultOrderForWidget(widgetKey)];
  };

  // Shared payload builder for userdetail_widgets. Always carries the persisted
  // `settings` map (merged with any overrides) so that a column-only save can't
  // clobber per-widget settings — the backend replace-merges at userdetail_widgets.
  const buildWidgetsPayload = (settingsOverride?: Record<string, any>) => {
    const persistedSettings = extendedUser?.settings?.userdetail_widgets?.settings || {};
    return {
      left_col: leftCol,
      mid_col: midCol,
      right_col: rightCol,
      settings: { ...persistedSettings, ...(settingsOverride || {}) }
    };
  };

  const SettingsComponent = settingsOpenFor ? getWidgetSettingsComponent(settingsOpenFor) : null;

  // Read-only status for a placed widget, or null when it has none.
  const renderWidgetStatus = (widgetKey: string) => {
    const StatusComponent = getWidgetStatusComponent(widgetKey);
    if (!StatusComponent) return null;
    return <StatusComponent
      value={getWidgetSettings(widgetKey)}
      order={HelpersClientWidgets.getOrderKeyForWidget(widgetKey) ? getWidgetOrder(widgetKey) : undefined}
    />;
  };




  const Draggable = (props:any) => {
    const { ref } = useDraggable({
      id: props.id,
    });

    return (
      <Box
        ref={ref}
        sx={{ cursor: 'pointer' }}
      >
        {props.children}
      </Box>
    );
  };
  const Droppable = (props:any) => {
    const { isDropTarget, ref } = useDroppable({
      id: props.id,
    });

    return (
      <Box ref={ref}>
        {typeof props.children === 'function' ? props.children(isDropTarget) : props.children}
      </Box>
    );
  };
  const widgetBox = (name: string, withMargin?: boolean, deleteableFrom?: string, deletableIndex?: number, widgetKey?: string) => {
    const hasSettings = !!widgetKey && widgetsWithSettings.includes(widgetKey);
    // right-side reservation keeps long left-aligned titles clear of the
    // top-right button cluster (gear + remove)
    const reserve = hasSettings ? 10 : 5;
    return <Box sx={{
      border: '1px solid transparent',
      borderColor: theme.palette.primary.main,
      padding: 1,
      fontSize: '0.8rem',
      borderRadius: 0.5,
      position: 'relative',
      marginTop: !!withMargin ? 2 : 0,
      marginBottom: !!withMargin ? 2 : 0
    }}>
      <Typography sx={{
        fontStyle: 'italic',
        textAlign: !!deleteableFrom ? 'left' : 'center',
        pr: !!deleteableFrom ? reserve : 0
      }}>{t(name)}</Typography>
      <Divider sx={{
        marginTop: 0.5
      }} />

      {!!deleteableFrom && (() => {
        const status = !!widgetKey ? renderWidgetStatus(widgetKey) : null;
        return <>
          {status ?? <Typography sx={{
            fontStyle: 'italic',
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            marginTop: 1
          }}>{t("coach.personal_settings.userdetail_widgets.no_settings")}</Typography>}
        </>;
      })()}

      {!!deleteableFrom && <Box sx={{
        position: 'absolute',
        top: 4,
        right: 4,
        display: 'flex',
        gap: 0.5
      }}>
        {hasSettings && <IconButton
          title={t("general.settings")}
          onClick={() => {
            // seed the drafts from persisted settings, then open the dialog
            setSettingsDraft(getWidgetSettings(widgetKey as string));
            setSettingsOrderDraft(
              HelpersClientWidgets.getOrderKeyForWidget(widgetKey as string)
                ? getWidgetOrder(widgetKey as string)
                : null
            );
            setSettingsOpenFor(widgetKey || null);
          }}
          sx={{padding: '6px'}}
        >
          <Settings sx={{fontSize: '1.3rem', color: theme.palette.primary.main}} />
        </IconButton>}
        <IconButton
          title={t("general.remove")}
          onClick={() => {
            if (deleteableFrom === "left") {
              let nlc = [...leftCol];
              nlc.splice(deletableIndex || 0, 1);
              setLeftCol(nlc);
            }
            if (deleteableFrom === "mid") {
              let nmc = [...midCol];
              nmc.splice(deletableIndex || 0, 1);
              setMidCol(nmc);
            }
            if (deleteableFrom === "right") {
              let nrc = [...rightCol];
              nrc.splice(deletableIndex || 0, 1);
              setRightCol(nrc);
            }
          }}
          sx={{padding: '6px'}}
        >
          <Close sx={{fontSize: '1.3rem', color: theme.palette.secondary.main}} />
        </IconButton>
      </Box>}
    </Box>
  }
  const dropHere = (id:string) => {
    return <Droppable id={id}>
      {(isDropTarget:boolean) => (
        <Box sx={{
          padding: 2,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          border: '2px dashed',
          borderColor: isDropTarget ? theme.palette.primary.main : theme.palette.grey[300],
          backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'rgb(244,248,247)',
          borderRadius: 0.5,
          transition: 'border-color 0.15s ease-in-out, background-color 0.15s ease-in-out'
        }}>
          <Typography sx={{
            fontSize: '0.8rem',
            color: isDropTarget ? theme.palette.primary.main : theme.palette.text.secondary
          }}>{t("general.droppable.drop_here")}</Typography>
        </Box>
      )}
    </Droppable>
  }










  useEffect(() => {
    if (!!extendedUserTemp) {
      setExtendedUser(extendedUserTemp);
      if (!!extendedUserTemp?.settings?.userdetail_widgets) {
        setLeftCol(extendedUserTemp?.settings?.userdetail_widgets?.left_col || []);
        setMidCol(extendedUserTemp?.settings?.userdetail_widgets?.mid_col || []);
        setRightCol(extendedUserTemp?.settings?.userdetail_widgets?.right_col || []);
      }
    } 
  }, [extendedUserTemp]);
  useEffect(() => {
    if (notTheFirstTime > 1 && !!extendedUser && [...leftCol, ...midCol, ...rightCol].length > 0) mutationSave.mutate({
      formData: {
        object: 'user',
        id: extendedUser.id,
        fields: fields,
        ob: {
          id: extendedUser.id,
          extra_data: {
            userdetail_widgets: buildWidgetsPayload()
          }
        }
      }
    });
    setNotTheFirstTime(notTheFirstTime + 1);
  }, [leftCol, midCol, rightCol]);

  return <Grid container spacing={2}>
    <DragDropProvider
      onDragEnd={(event) => {
        if (event.canceled) return;

        const {target, source} = event.operation;

        if (target?.id === 'leftTop' && !!source) {
          setLeftCol([source.id?.toString(), ...leftCol]);
        }
        if (target?.id === 'midTop' && !!source) {
          setMidCol([source.id?.toString(), ...midCol]);
        }
        if (target?.id === 'rightTop' && !!source) {
          setRightCol([source.id?.toString(), ...rightCol]);
        }
        if ((target?.id?.toString() || '').indexOf("left_") > -1 && !!source) {
          let nlc = [...leftCol];
          nlc.splice(parseInt((target?.id?.toString() || '').split("_")[1], 10) + 1, 0, source.id?.toString());
          setLeftCol(nlc);
          
        }
        if ((target?.id?.toString() || '').indexOf("mid_") > -1 && !!source) {
          let nmc = [...midCol];
          nmc.splice(parseInt((target?.id?.toString() || '').split("_")[1], 10) + 1, 0, source.id?.toString());
          setMidCol(nmc);
          
        }
        if ((target?.id?.toString() || '').indexOf("right_") > -1 && !!source) {
          let nrc = [...rightCol];
          nrc.splice(parseInt((target?.id?.toString() || '').split("_")[1], 10) + 1, 0, source.id?.toString());
          setRightCol(nrc);
          
        }
        return;
      }}
    >
      {!!extendedUserLoading && <Grid item xs={12} sx={{textAlign: 'center'}}>
        <CircularProgress />
      </Grid>}
      {!!extendedUser && <>
        <Grid item xs={12} md={9}>
          <Paper sx={{
            padding: 2
          }}>
            <Grid container spacing={1}>
              <Grid item xs={4} sx={{
                textAlign: 'center',
                borderRight: '1px solid lightgrey',
                paddingRight: 1
              }}>
                {dropHere("leftTop")}
                {leftCol.map((cl, ii) => <>
                  {widgetBox(`coach.personal_settings.userdetail_widgets.widgets.${cl}`, true, "left", ii, cl)}
                  {dropHere(`left_${ii}`)}
                </>)}
              </Grid>
              <Grid item xs={4} sx={{
                textAlign: 'center',
                borderRight: '1px solid lightgrey',
                paddingleft: 1,
                paddingRight: 1
              }}>
                {dropHere("midTop")}
                {midCol.map((cl, ii) => <>
                  {widgetBox(`coach.personal_settings.userdetail_widgets.widgets.${cl}`, true, "mid", ii, cl)}
                  {dropHere(`mid_${ii}`)}
                </>)}
              </Grid>
              <Grid item xs={4} sx={{
                textAlign: 'center',
                paddingleft: 1
              }}>
                {dropHere("rightTop")}
                {rightCol.map((cl, ii) => <>
                  {widgetBox(`coach.personal_settings.userdetail_widgets.widgets.${cl}`, true, "right", ii, cl)}
                  {dropHere(`right_${ii}`)}
                </>)}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Grid item xs={12}>
            <Paper sx={{
              padding: 2
            }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography sx={{fontWeight: 'bold'}}>{t("coach.personal_settings.userdetail_widgets.widget_categories.basic")}</Typography>
                  <Divider />
                </Grid>
                {[
                  "picture_and_tags", "details_basic", "details_contact", "details_extra",
                  "consent_information", "notification_information", "goto_links", "actions",
                  "account_mgmnt"
                  , "internal_comment"
                ].map(k => <>
                  {([...leftCol, ...midCol, ...rightCol]).indexOf(k) < 0 && <Grid item xs={12}>
                    <Draggable id={k}>
                      {widgetBox(`coach.personal_settings.userdetail_widgets.widgets.${k}`)}
                    </Draggable>
                  </Grid>}
                </>)}
                <Grid item xs={12} sx={{
                  marginTop: 1
                }}>
                  <Typography sx={{fontWeight: 'bold'}}>{t("coach.personal_settings.userdetail_widgets.widget_categories.modules")}</Typography>
                  <Divider />
                </Grid>
                {[
                  "appointments_small", "courses_small", "diary_small", "files_small", "measures_small",
                  "mollie_links_small", "questionnaires_small", "weight_trigger",
                  "communication_nutri", "communication_ehealth", "communication_zorg",
                  "basic_intake", "basic_treatment", "basic_advice", "basic_measure_uploads",
                  "timeline", "basic_advice_uploads", "basic_sleep", "workshops"
                  , "status", "calculation"
                ].map(k => <>
                  {([...leftCol, ...midCol, ...rightCol]).indexOf(k) < 0 && <Grid item xs={12}>
                    <Draggable id={k}>
                      {widgetBox(`coach.personal_settings.userdetail_widgets.widgets.${k}`)}
                    </Draggable>
                  </Grid>}
                </>)}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </>}
    </DragDropProvider>

    <SnackSaved open={savedOpen} setOpen={setSavedOpen} />

    {!!SettingsComponent && <Base
      name="widget_settings"
      open={!!settingsOpenFor}
      setOpen={(b) => {
        if (!b) {
          setSettingsOpenFor(null);
          setSettingsDraft(null);
          setSettingsOrderDraft(null);
        }
      }}
      title={t(`coach.personal_settings.userdetail_widgets.widgets.${settingsOpenFor}`)}
      fullWidth
      maxWidth="sm"
      content={<Box sx={{paddingTop: 1}}>
        <SettingsComponent
          value={settingsDraft}
          onChange={(v:any) => setSettingsDraft(v)}
          order={settingsOrderDraft || []}
          onOrderChange={(v:string[]) => setSettingsOrderDraft(v)}
        />
      </Box>}
      actions={<DialogActions sx={{
        borderTop: '1px solid transparent',
        borderColor: theme.palette.primary.main
      }}>
        <MuiButton autoFocus onClick={() => {
          setSettingsOpenFor(null);
          setSettingsDraft(null);
          setSettingsOrderDraft(null);
        }}>{t("client.general.buttons.back")}</MuiButton>
        <MuiButton variant="contained" disabled={mutationSave.isPending} onClick={() => {
          if (!!extendedUser && !!settingsOpenFor) {
            mutationSave.mutate({
              formData: {
                object: 'user',
                id: extendedUser.id,
                fields: fields,
                ob: {
                  id: extendedUser.id,
                  extra_data: {
                    userdetail_widgets: buildWidgetsPayload({
                      [settingsOpenFor]: settingsDraft,
                      ...(widgetOrderKey && settingsOrderDraft
                        ? { [widgetOrderKey]: settingsOrderDraft }
                        : {})
                    })
                  }
                }
              }
            });
            setSettingsOpenFor(null);
            setSettingsDraft(null);
            setSettingsOrderDraft(null);
          }
        }}>{t("general.confirm.actions.save")}</MuiButton>
      </DialogActions>}
    />}
  </Grid>;
}

